import api from './api';

// Tipos de Assinatura
export interface Subscription {
  status: 'ACTIVE_TRIAL' | 'ACTIVE_PAID' | 'EXPIRED_TRIAL' | 'CANCELLED' | 'INACTIVE';
  trialEndDate?: string;
  subscriptionEndDate?: string;
  planId?: string;
  billingId?: string;
}

export interface CheckoutSession {
  url: string;
  billingId: string;
}

// Tipos do AbacatePay
export interface AbacatePayBilling {
  id: string;
  url: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'EXPIRED' | 'CANCELLED' | 'REFUNDED';
  methods: string[];
  frequency: string;
  customer: {
    id: string;
    metadata: {
      email: string;
      name?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateBillingResponse {
  error?: string;
  billing?: AbacatePayBilling;
}

export interface CreateBillingRequest {
  planId?: string; // ID do plano selecionado
  planType?: string; // Deprecated: usar planId
  customerEmail: string;
  customerName: string;
  customerTaxId: string; // CPF (11 dígitos) ou CNPJ (14 dígitos)
  customerCellphone?: string; // Telefone celular (opcional)
  paymentMethod?: 'PIX' | 'CREDIT_CARD'; // Método de pagamento (padrão: PIX)
}

// Tipos de Plano
export interface Plan {
  id: string;
  name: string;
  externalId: string;
  totalPriceCents: number;
  totalPriceBRL: number;
  installments: number;
  installmentPriceBRL: number;
  accessPeriodMonths: number;
  features: string[];
  description: string;
  maxUsers?: number; // Número máximo de usuários permitidos
}

// Tipos de uso de assinatura
export interface SubscriptionUsage {
  hasActiveSubscription: boolean;
  currentUsers: number;
  maxUsers: number | null;
  planName: string;
  planId: string | null;
  percentageUsed: number;
  subscriptionStatus?: string;
}

/**
 * Busca todos os planos disponíveis
 */
export const getPlans = async (): Promise<Plan[]> => {
  try {
    const response = await api.get('/api/plans');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar planos:', error);
    throw error;
  }
};

/**
 * Busca um plano específico por ID
 */
export const getPlanById = async (planId: string): Promise<Plan> => {
  try {
    const response = await api.get(`/api/plans/${planId}`);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar plano:', error);
    throw error;
  }
};

/**
 * Cria uma nova cobrança no AbacatePay
 */
export const createBilling = async (data: CreateBillingRequest): Promise<CreateBillingResponse> => {
  try {
    console.log('=== SERVICE: createBilling ===');
    console.log('Dados recebidos:', data);
    console.log('URL da API:', api.defaults.baseURL);
    
    const response = await api.post('/api/payment/create-billing', data);
    
    console.log('Resposta da API:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro no serviço createBilling:', error);
    console.error('Response error:', error.response?.data);
    console.error('Status:', error.response?.status);
    throw error;
  }
};

/**
 * Lista todas as cobranças
 */
export const listBillings = async (): Promise<{ billings: AbacatePayBilling[] }> => {
  try {
    const response = await api.get('/api/payment/billings');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao listar cobranças:', error);
    throw error;
  }
};

/**
 * Busca uma cobrança específica
 */
export const getBilling = async (billingId: string): Promise<{ billing: AbacatePayBilling }> => {
  try {
    const response = await api.get(`/api/payment/billing/${billingId}`);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar cobrança:', error);
    throw error;
  }
};

/**
 * Verifica o health da integração
 */
export const checkPaymentHealth = async (): Promise<{ status: string; message: string }> => {
  try {
    const response = await api.get('/api/payment/health');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao verificar health:', error);
    throw error;
  }
};

/**
 * Busca a assinatura do usuário atual
 */
export const getSubscription = async (): Promise<Subscription> => {
  try {
    const response = await api.get('/api/subscription');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar assinatura:', error);
    throw error;
  }
};

/**
 * Cria uma sessão de checkout para assinatura
 */
export const createCheckoutSession = async (): Promise<CheckoutSession> => {
  try {
    const response = await api.post('/api/subscription/checkout');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao criar sessão de checkout:', error);
    throw error;
  }
};

/**
 * Busca informações de uso da assinatura atual
 */
export const getSubscriptionUsage = async (): Promise<SubscriptionUsage> => {
  try {
    const response = await api.get('/api/subscription/usage');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar uso da assinatura:', error);
    throw error;
  }
};

/**
 * Cancela a assinatura do usuário
 */
export const cancelSubscription = async (): Promise<{ message: string }> => {
  try {
    const response = await api.post('/api/subscription/cancel');
    return response.data;
  } catch (error: any) {
    console.error('Erro ao cancelar assinatura:', error);
    throw error;
  }
};
